/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

function SEO(props) {
  const {
    description,
    lang,
    meta,
    title,
    image,
    pathname = "/",
    type = "website",
    datePublished,
    dateModified,
    keywords = [],
    noindex = false,
  } = props
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            domain
            title
            author
            description
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const domain = site.siteMetadata.domain.replace(/\/$/, "")
  const absoluteUrl = value => {
    if (/^https?:\/\//.test(value)) return value
    return `${domain}${value.startsWith("/") ? value : `/${value}`}`
  }
  const canonicalUrl = absoluteUrl(pathname)
  const metaImage = absoluteUrl(image || "/favicons/favicon-512.png")
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: site.siteMetadata.title,
        url: domain,
        description: site.siteMetadata.description,
      },
      {
        "@type": "Person",
        name: site.siteMetadata.author,
        url: domain,
      },
    ],
  }

  if (type === "article") {
    structuredData["@graph"].push({
      "@type": "BlogPosting",
      headline: title,
      description: metaDescription,
      url: canonicalUrl,
      mainEntityOfPage: canonicalUrl,
      datePublished,
      dateModified: dateModified || datePublished,
      image: metaImage,
      author: {
        "@type": "Person",
        name: site.siteMetadata.author,
        url: domain,
      },
      publisher: {
        "@type": "Person",
        name: site.siteMetadata.author,
      },
    })
  }

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={[
        {
          rel: `canonical`,
          href: canonicalUrl,
        },
        {
          rel: `icon`,
          type: `image/x-icon`,
          href: `/favicons/favicon.ico`,
        },
        {
          rel: `icon`,
          type: `image/png`,
          sizes: `16x16`,
          href: `/favicons/favicon-16x16.png`,
        },
        {
          rel: `icon`,
          type: `image/png`,
          sizes: `32x32`,
          href: `/favicons/favicon-32x32.png`,
        },
        {
          rel: `apple-touch-icon`,
          sizes: `180x180`,
          href: `/favicons/apple-touch-icon.png`,
        },
        {
          rel: `manifest`,
          href: `/site.webmanifest`,
        },
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `author`,
          content: site.siteMetadata.author,
        },
        {
          name: `robots`,
          content: noindex ? "noindex, nofollow" : "index, follow",
        },
        ...(keywords.length
          ? [
              {
                name: "keywords",
                content: keywords.join(", "),
              },
            ]
          : []),
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:site_name`,
          content: site.siteMetadata.title,
        },
        {
          property: `og:url`,
          content: canonicalUrl,
        },
        {
          property: `og:locale`,
          content: `en_US`,
        },
        {
          property: `og:image`,
          content: metaImage,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: type,
        },
        ...(type === "article" && datePublished
          ? [
              {
                property: "article:published_time",
                content: datePublished,
              },
              {
                property: "article:modified_time",
                content: dateModified || datePublished,
              },
            ]
          : []),
        {
          name: `twitter:card`,
          content: image ? `summary_large_image` : `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: metaImage,
        },
      ].concat(meta)}
      script={[
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(structuredData),
        },
      ]}
    />
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
  keywords: [],
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  pathname: PropTypes.string,
  type: PropTypes.oneOf(["website", "article"]),
  datePublished: PropTypes.string,
  dateModified: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  noindex: PropTypes.bool,
}

export default SEO
